import { request } from '@/renderer/utils/api';

// Web Push (VAPID) subscription plumbing (#1669). After the priming prompt +
// granted OS permission, this subscribes the browser to background push through
// the service worker and stores the subscription server-side so a release
// warning can wake the SW even when the tab / installed PWA is closed. The
// local Notification path (DesktopNotifications.openNotification) stays as-is
// for foreground; this is the additive background channel.

const VAPID_PUBLIC_KEY = `${import.meta.env.VITE_PUBLIC_ENV__VAPID_PUBLIC_KEY ?? ''}`;

const SUBSCRIBE_REQUEST = {
  input: '/api/v1/webPushSubscription',
  method: 'POST' as const,
};
const UNSUBSCRIBE_REQUEST = {
  input: '/api/v1/webPushSubscription',
  method: 'DELETE' as const,
};

/**
 * The VAPID public key is base64url text; PushManager.subscribe wants it as a
 * Uint8Array (the raw application server key). Standard conversion.
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) {
    output[i] = raw.charCodeAt(i);
  }
  return output;
}

/** Web Push is usable in this browser and we have a key to subscribe with. */
export function webPushSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    typeof window !== 'undefined' &&
    'PushManager' in window &&
    !!VAPID_PUBLIC_KEY
  );
}

/**
 * Whether THIS browser currently holds a Web Push subscription. Per-browser
 * state (unlike native push, which is per-account), so the settings toggle
 * reads it live rather than from a server field. Returns false when unsupported
 * or when no service worker is registered (e.g. dev, where the SW is disabled).
 */
export async function isWebPushSubscribed(): Promise<boolean> {
  if (!webPushSupported()) {
    return false;
  }
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) {
      return false;
    }
    const subscription = await registration.pushManager.getSubscription();
    return !!subscription;
  } catch {
    return false;
  }
}

/** POST a subscription to the server (idempotent upsert by endpoint). */
async function storeSubscription(subscription: PushSubscription): Promise<void> {
  const json = subscription.toJSON();
  const keys = json.keys as { p256dh?: string; auth?: string } | undefined;

  if (!json.endpoint || !keys?.p256dh || !keys?.auth) {
    return;
  }

  await request(SUBSCRIBE_REQUEST, {
    hideAlert: true,
    body: {
      data: {
        endpoint: json.endpoint,
        keys: { p256dh: keys.p256dh, auth: keys.auth },
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      },
    },
  });
}

/**
 * Subscribe this browser to background Web Push and store the subscription
 * server-side. Safe to call repeatedly — it reuses an existing subscription
 * when the app-server key still matches, and re-subscribes if the key rotated
 * (a stale subscription can never receive our sends). No-op when unsupported or
 * when notification permission hasn't been granted. Never throws — background
 * push is non-essential to app function; failures are logged.
 */
export async function subscribeToWebPush(): Promise<boolean> {
  if (!webPushSupported()) {
    return false;
  }
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    const applicationServerKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);

    const existing = await registration.pushManager.getSubscription();
    if (existing) {
      // If the existing subscription was created with a different app-server key
      // (rotated VAPID key), it can no longer receive our sends — drop it and
      // create a fresh one. Otherwise reuse it and just refresh the server row.
      const existingKey = existing.options?.applicationServerKey;
      const sameKey =
        !!existingKey &&
        new Uint8Array(existingKey as ArrayBuffer).toString() === applicationServerKey.toString();

      if (sameKey) {
        await storeSubscription(existing);
        return true;
      }

      await existing.unsubscribe().catch(() => undefined);
    }

    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey,
    });
    await storeSubscription(subscription);
    return true;
  } catch (error) {
    console.warn('web push: subscribe failed', error);
    return false;
  }
}

/**
 * Unsubscribe this browser from background Web Push: remove the subscription
 * server-side and locally. Called when the user disables notifications. Never
 * throws.
 */
export async function unsubscribeFromWebPush(): Promise<void> {
  if (!webPushSupported()) {
    return;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      return;
    }

    const endpoint = subscription.toJSON().endpoint;
    if (endpoint) {
      await request(UNSUBSCRIBE_REQUEST, {
        hideAlert: true,
        body: { data: { endpoint } },
      }).catch((error) => console.warn('web push: server unsubscribe failed', error));
    }

    await subscription.unsubscribe().catch(() => undefined);
  } catch (error) {
    console.warn('web push: unsubscribe failed', error);
  }
}
